import type { AgentSessionViewModel } from "@inf-monkeys-tech/monkeys";
import {
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleStop,
  Clock3,
  Download,
  FileOutput,
  Loader2,
  XCircle,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import { useMonkeysLocaleMessages } from "../../provider";
import { cn } from "../layout";
import { Button } from "../primitives";
import {
  createAgentWorkbenchActivityModel,
  type AgentWorkbenchEventOf,
} from "./activity-model";
import {
  AgentWorkbenchApproval,
  type AgentWorkbenchApprovalIntent,
} from "./AgentWorkbenchApproval";
import { AgentWorkbenchTool } from "./AgentWorkbenchTool";

export interface AgentWorkbenchArtifactIntent {
  artifactId: string;
  name: string;
  url: string;
}

export interface AgentWorkbenchActivityClassNames {
  root?: string;
  item?: string;
  trigger?: string;
  content?: string;
  approval?: string;
  artifact?: string;
  usage?: string;
}

export interface AgentWorkbenchActivityProps {
  viewModel?: AgentSessionViewModel;
  ariaLabel?: string;
  hiddenToolCallIds?: ReadonlySet<string>;
  hideReasoning?: boolean;
  hideStoppedRun?: boolean;
  className?: string;
  classNames?: AgentWorkbenchActivityClassNames;
  onApprovalDecision?: (intent: AgentWorkbenchApprovalIntent) => void;
  onArtifactOpen?: (intent: AgentWorkbenchArtifactIntent) => void;
}

interface ActivityDetailsProps {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  open?: boolean;
  streaming?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  children: ReactNode;
}

function ActivityDetails({
  icon: Icon,
  title,
  open,
  streaming,
  className,
  triggerClassName,
  contentClassName,
  children,
}: ActivityDetailsProps) {
  return (
    <details
      className={cn(
        "group min-w-0 overflow-hidden rounded-lg border border-border bg-card text-card-foreground",
        className,
      )}
      open={open}
    >
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden",
          triggerClassName,
        )}
      >
        {streaming ? (
          <Loader2
            className="size-4 shrink-0 animate-spin"
            aria-hidden="true"
          />
        ) : (
          <Icon
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        <ChevronDown
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div
        className={cn(
          "min-w-0 border-t border-border px-3 py-3 text-sm",
          contentClassName,
        )}
      >
        {children}
      </div>
    </details>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === "completed" || status === "passed") {
    return <CheckCircle2 className="size-4 text-success" aria-hidden="true" />;
  }
  if (status === "failed") {
    return (
      <XCircle className="size-4 text-destructive-text" aria-hidden="true" />
    );
  }
  if (status === "running") {
    return (
      <Clock3
        className="size-4 animate-pulse text-warning-foreground"
        aria-hidden="true"
      />
    );
  }
  return <Circle className="size-4 text-muted-foreground" aria-hidden="true" />;
}

function formatDuration(durationMs: number): string {
  if (durationMs < 1_000) return "<1s";
  const totalSeconds = Math.floor(durationMs / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function RunStatus({
  event,
}: {
  event: AgentWorkbenchEventOf<"status"> & { resolvedDurationMs?: number };
}) {
  const messages = useMonkeysLocaleMessages().agentWorkbench;
  const status = event.payload.status;
  if (status !== "stopping" && status !== "stopped") return null;

  return (
    <div
      className="flex items-center gap-2 text-sm text-muted-foreground"
      role="status"
    >
      {status === "stopping" ? (
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      ) : (
        <CircleStop className="size-4" aria-hidden="true" />
      )}
      <span>
        {status === "stopping"
          ? messages.run.stopping
          : messages.run.stoppedAfter({
              duration: formatDuration(
                event.payload.durationMs ?? event.resolvedDurationMs ?? 0,
              ),
            })}
      </span>
    </div>
  );
}

export function AgentWorkbenchActivity({
  viewModel,
  ariaLabel,
  hiddenToolCallIds,
  hideReasoning = false,
  hideStoppedRun = false,
  className,
  classNames,
  onApprovalDecision,
  onArtifactOpen,
}: AgentWorkbenchActivityProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench;
  if (!viewModel) return null;

  const activity = createAgentWorkbenchActivityModel(viewModel);
  const reasoning = hideReasoning ? [] : activity.reasoning;
  const tools = activity.tools.filter(
    (event) => !hiddenToolCallIds?.has(event.payload.toolCallId),
  );
  const approvals = activity.approvals.filter(
    (event) =>
      !event.payload.toolCallId ||
      !hiddenToolCallIds?.has(event.payload.toolCallId),
  );
  const usage = activity.usage?.payload;
  const contextWindow =
    typeof usage?.contextWindow === "number" && usage.contextWindow > 0
      ? usage.contextWindow
      : undefined;
  const showRun =
    activity.run?.payload.status === "stopping" ||
    (activity.run?.payload.status === "stopped" && !hideStoppedRun);
  const hasActivity =
    reasoning.length > 0 ||
    activity.plans.length > 0 ||
    activity.tasks.length > 0 ||
    tools.length > 0 ||
    approvals.length > 0 ||
    activity.artifacts.length > 0 ||
    showRun ||
    contextWindow !== undefined;
  if (!hasActivity) return null;

  return (
    <section
      className={cn("space-y-3", classNames?.root, className)}
      aria-label={ariaLabel ?? messages.label}
      data-agent-workbench-mode={activity.mode}
      data-agent-workbench-status={activity.status}
    >
      {reasoning.map((event) => (
        <ActivityDetails
          key={event.payload.reasoningId}
          icon={Circle}
          title={messages.reasoning}
          open={event.payload.status === "streaming"}
          streaming={event.payload.status === "streaming"}
          className={classNames?.item}
          triggerClassName={classNames?.trigger}
          contentClassName={classNames?.content}
        >
          <div
            tabIndex={0}
            className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-muted-foreground [overflow-wrap:anywhere]"
          >
            {event.payload.text}
          </div>
        </ActivityDetails>
      ))}

      {activity.plans.map((event) => (
        <ActivityDetails
          key={event.payload.planId}
          icon={Circle}
          title={event.payload.title || messages.plan}
          open={event.payload.status === "running"}
          streaming={event.payload.status === "running"}
          className={classNames?.item}
          triggerClassName={classNames?.trigger}
          contentClassName={classNames?.content}
        >
          <ol className="space-y-2">
            {event.payload.steps.map((step) => (
              <li key={step.id} className="flex min-w-0 items-start gap-2">
                <StatusIcon status={step.status} />
                <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                  {step.title}
                </span>
              </li>
            ))}
          </ol>
        </ActivityDetails>
      ))}

      {activity.tasks.map((event) => (
        <ActivityDetails
          key={event.payload.taskId}
          icon={Circle}
          title={event.payload.title}
          open={event.payload.status === "running"}
          streaming={event.payload.status === "running"}
          className={classNames?.item}
          triggerClassName={classNames?.trigger}
          contentClassName={classNames?.content}
        >
          <div className="flex items-start gap-2">
            <StatusIcon status={event.payload.status} />
            <span className="min-w-0 whitespace-pre-wrap break-words text-muted-foreground [overflow-wrap:anywhere]">
              {event.payload.detail || messages.taskNoDetail}
            </span>
          </div>
        </ActivityDetails>
      ))}

      {tools.map((event) => (
        <AgentWorkbenchTool
          key={event.payload.toolCallId}
          event={event}
          defaultExpanded={event.payload.status === "failed"}
          classNames={{
            root: classNames?.item,
            trigger: classNames?.trigger,
            content: classNames?.content,
          }}
        />
      ))}

      {approvals.map((event) => {
        return (
          <AgentWorkbenchApproval
            key={event.payload.approvalId}
            approval={event.payload}
            className={classNames?.approval}
            onDecision={onApprovalDecision}
          />
        );
      })}

      {activity.artifacts.map((event) => {
        const canOpen = Boolean(event.payload.url && onArtifactOpen);
        return (
          <article
            key={event.payload.artifactId}
            className={cn(
              "flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-card-foreground",
              classNames?.artifact,
            )}
          >
            <FileOutput
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {event.payload.name}
              </div>
              <div className="text-xs text-muted-foreground">
                {messages.artifact[event.payload.status]}
              </div>
            </div>
            {event.payload.url ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                aria-label={messages.artifact.open({
                  name: event.payload.name,
                })}
                disabled={!canOpen}
                onClick={() => {
                  if (!event.payload.url) return;
                  onArtifactOpen?.({
                    artifactId: event.payload.artifactId,
                    name: event.payload.name,
                    url: event.payload.url,
                  });
                }}
              >
                <Download className="size-4" aria-hidden="true" />
              </Button>
            ) : null}
          </article>
        );
      })}

      {showRun && activity.run ? <RunStatus event={activity.run} /> : null}

      {usage && contextWindow !== undefined ? (
        <div
          className={cn("flex justify-end", classNames?.usage)}
          aria-label={messages.usage.label}
        >
          <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-foreground">
            {messages.usage.context({
              used: usage.contextTokens ?? usage.totalTokens ?? 0,
              total: contextWindow,
            })}
          </span>
        </div>
      ) : null}
    </section>
  );
}
